import { useEffect, useState } from 'react';
import { supabase } from '@/integrations/supabase/client';
import type { Session } from '@supabase/supabase-js';

// Module-level cache so navigating between admin pages doesn't re-show the
// "טוען…" screen while we recheck the role. Keyed by user id.
const roleCache = new Map<string, boolean>();
let lastUserId: string | null = null;

export function useAdminAuth() {
  const [session, setSession] = useState<Session | null>(null);
  const [isAdmin, setIsAdmin] = useState<boolean | null>(null);
  const [sessionLoading, setSessionLoading] = useState(true);
  const [roleLoading, setRoleLoading] = useState(false);


  useEffect(() => {
    let mounted = true;

    const applySession = (s: Session | null) => {
      if (!mounted) return;
      const newUid = s?.user?.id ?? null;
      setSession(s);
      setSessionLoading(false);
      // Only reset role state when the actual user identity changes.
      // Token refresh / tab-focus events keep the same uid → don't flicker.
      if (newUid !== lastUserId) {
        lastUserId = newUid;
        if (newUid && roleCache.has(newUid)) {
          setIsAdmin(roleCache.get(newUid)!);
        } else {
          setIsAdmin(newUid ? null : false);
        }
      }
    };

    const { data: sub } = supabase.auth.onAuthStateChange((_evt, s) => applySession(s));
    supabase.auth.getSession().then(({ data }) => applySession(data.session));

    return () => {
      mounted = false;
      sub.subscription.unsubscribe();
    };
  }, []);

  useEffect(() => {
    let cancelled = false;
    const uid = session?.user?.id;
    if (sessionLoading) return;
    if (!uid) {
      setIsAdmin(false);
      setRoleLoading(false);
      return;
    }
    if (roleCache.has(uid)) {
      setIsAdmin(roleCache.get(uid)!);
      setRoleLoading(false);
      return;
    }
    setRoleLoading(true);
    (async () => {
      const { data, error } = await supabase
        .from('user_roles')
        .select('role')
        .eq('user_id', uid)
        .eq('role', 'admin')
        .maybeSingle();
      if (cancelled) return;
      const ok = !error && !!data;
      roleCache.set(uid, ok);
      setIsAdmin(ok);
      setRoleLoading(false);
    })();
    return () => {
      cancelled = true;
    };
  }, [session?.user?.id, sessionLoading]);

  return {
    session,
    isAdmin,
    loading: sessionLoading || roleLoading || (!!session?.user && isAdmin === null),
  };
}
